import { useEffect } from "react";
import { Map } from "ol";
import MapBrowserEvent from "ol/MapBrowserEvent";
import { Circle, LineString, MultiPoint, Point, Polygon } from "ol/geom";
import BaseVectorLayer from "ol/layer/BaseVector";
import type { Pixel } from "ol/pixel";
import { isSelectableLayer } from "./excludeFromSelection";

/**
 * hover hit-detection 은 프레임당 최대 1회, 그 프레임의 마지막 포인터 위치로만 수행한다.
 * 시간 간격 타이머(throttle)는 리프레시 주기와 어긋나 커서 갱신이 프레임을 건너뛰므로 쓰지 않는다.
 */
export function useHoverCursor(mapRefObj: Map) {
  useEffect(() => {
    let pendingPixel: Pixel | null = null;
    let frame = 0;

    const applyHoverCursor = (pixel: Pixel) => {
      const target = mapRefObj.getTargetElement();
      if (!target) return;

      target.style.cursor = "default";

      // 벡터(어노테이션) 레이어로만 hit-detection 을 한정해 표시 레이어 수에 비례하던
      // 포인터 비용을 줄인다(타일/이미지 레이어는 hit-detection 대상에서 제외).
      const hit = mapRefObj.forEachFeatureAtPixel(
        pixel,
        (feature) => {
          const geometry = feature.getGeometry();
          return (
            geometry instanceof Point ||
            geometry instanceof MultiPoint ||
            geometry instanceof Polygon ||
            geometry instanceof LineString ||
            geometry instanceof Circle
          );
        },
        {
          layerFilter: (layer) =>
            layer instanceof BaseVectorLayer && isSelectableLayer(layer),
        }
      );

      if (hit) {
        target.style.cursor = "pointer";
      }
    };

    const onFrame = () => {
      frame = 0;
      if (!pendingPixel) return;
      const pixel = pendingPixel;
      pendingPixel = null;
      applyHoverCursor(pixel);
    };

    const handlePointerMove = (event: MapBrowserEvent<any>) => {
      const [x, y] = event.pixel;
      pendingPixel = [x, y];
      if (!frame) frame = requestAnimationFrame(onFrame);
    };

    mapRefObj.on("pointermove", handlePointerMove);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      mapRefObj.un("pointermove", handlePointerMove);
    };
  }, [mapRefObj]);
}
